import React, { useState, useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import { designTokens } from '@/styles/designTokens'

const spinAnimation = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const StatusContainer = styled.div`
  position: fixed;
  top: ${designTokens.spacing.lg};
  right: ${designTokens.spacing.lg};
  z-index: ${designTokens.zIndex.tooltip};
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing.sm};
  padding: ${designTokens.spacing.sm} ${designTokens.spacing.md};
  background: ${designTokens.colors.neutral.backgroundCard}CC;
  border-radius: ${designTokens.borderRadius.lg};
  color: ${designTokens.colors.text.primary};
  font-size: ${designTokens.typography.fontSize.sm};
  font-family: ${designTokens.typography.fontFamily.primary};
  font-weight: ${designTokens.typography.fontWeight.medium};
  backdrop-filter: blur(10px);
  border: 1px solid ${({ $statusColor }) => $statusColor};
  box-shadow: ${designTokens.shadows.md}, 0 0 15px ${({ $statusColor }) => $statusColor}20;
  cursor: ${({ $hasError }) => ($hasError ? 'pointer' : 'default')};
  transition: ${designTokens.transitions.normal};
`

const StatusIcon = styled.span`
  color: ${({ $statusColor }) => $statusColor};
  font-size: 1.2em;
  animation: ${({ $isSpinning }) => ($isSpinning ? spinAnimation : 'none')} 1.5s linear infinite;
  filter: drop-shadow(0 0 3px ${({ $statusColor }) => $statusColor});
  line-height: 1;
`

const StatusText = styled.span`
  letter-spacing: 0.5px;
`

const ErrorDetails = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  padding: ${designTokens.spacing.md};
  background: ${designTokens.colors.neutral.backgroundCard};
  border-radius: ${designTokens.borderRadius.lg};
  border: 1px solid ${designTokens.colors.status.error};
  max-width: 300px;
  font-size: ${designTokens.typography.fontSize.sm};
  font-family: ${designTokens.typography.fontFamily.primary};
  color: ${designTokens.colors.text.primary};
  box-shadow: ${designTokens.shadows.xl};
  word-break: break-word;
  opacity: 0;
  transform: translateY(-10px);
  animation: fadeIn 0.3s forwards;

  @keyframes fadeIn {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  strong {
    color: ${designTokens.colors.status.error};
  }
`

const ConnectionStatus = ({ connectionStatus, error }) => {
  const [reconnectCount, setReconnectCount] = useState(0)
  const [showDetails, setShowDetails] = useState(false)

  useEffect(() => {
    if (connectionStatus === 'reconnecting') {
      setReconnectCount(prev => prev + 1)
    } else if (connectionStatus === 'connected') {
      setReconnectCount(0)
    }
  }, [connectionStatus])

  const getStatusProps = () => {
    switch (connectionStatus) {
      case 'connected':
        return { color: designTokens.colors.status.success, text: 'Conectado', icon: '●' }
      case 'connecting':
        return { color: designTokens.colors.status.warning, text: 'Conectando...', icon: '⟳' }
      case 'reconnecting':
        const text = reconnectCount > 0 ? `Reconectando... (${reconnectCount})` : 'Reconectando...'
        return { color: designTokens.colors.status.warning, text, icon: '⟳' }
      case 'error':
        return { color: designTokens.colors.status.error, text: 'Erro de Conexão', icon: '✕' }
      case 'failed':
        return { color: designTokens.colors.status.error, text: 'Falha na Conexão', icon: '✕' }
      default:
        return { color: designTokens.colors.neutral.border, text: 'Desconectado', icon: '○' }
    }
  }

  const isSpinning = connectionStatus === 'connecting' || connectionStatus === 'reconnecting'
  const statusProps = getStatusProps()

  return (
    <StatusContainer
      $statusColor={statusProps.color}
      $hasError={!!error}
      onClick={() => error && setShowDetails(!showDetails)}
      title={error ? 'Clique para mais detalhes' : ''}
    >
      <StatusIcon
        $statusColor={statusProps.color}
        $isSpinning={isSpinning}
      >
        {statusProps.icon}
      </StatusIcon>
      <StatusText>{statusProps.text}</StatusText>

      {error && showDetails && (
        <ErrorDetails>
          <strong>Erro:</strong> {error}
        </ErrorDetails>
      )}
    </StatusContainer>
  )
}

export default ConnectionStatus
